import React from 'react';
import { useTranslation } from 'next-i18next';
import classnames from 'classnames';

import { Modal, Stepper } from '@components/ui';
import * as Notifications from '@common/Notification';
import { useAnalytics } from '@hooks/useAnalytics';
import { useStepper } from '@hooks/useStepper';
import { useTimer } from '@hooks/useTimer';

import PhoneVerificationModalNumberStep from './PhoneVerificationModalNumberStep';
import PhoneVerificationModalVerificationStep from './PhoneVerificationModalVerificationStep';

import styles from './PhoneVerificationModal.module.scss';

interface Props {
  className?: string,
  isOpen: boolean,
  initialPhoneNumber?: string,
  handleModalClose: () => void,
  onAfterClose: () => void,
  onSuccess?: () => void,
  mode: 'edit' | 'verify',
  withSuccessNotification?: boolean
}

const STEP_IDS = {
  PHONE_INPUT: 'PHONE_INPUT',
  CODE_VERIFICATION: 'CODE_VERIFICATION'
};
const TIMER_RESEND_SECONDS = 60;
const TIMER_DEFAULT_DATE = new Date();

const PhoneVerificationModal = (props: Props) => {

  const {
    className,
    isOpen,
    initialPhoneNumber,
    handleModalClose,
    onAfterClose,
    onSuccess,
    withSuccessNotification = true,
    mode = 'verify'
  } = props;

  const [phoneNumber, setPhoneNumber] = React.useState<string>(initialPhoneNumber || '');
  const { restart: restartTimer, isRunning: isTimerRunning, totalSeconds: timerTotalSeconds } = useTimer({ expiryTimestamp: TIMER_DEFAULT_DATE });
  const analytics = useAnalytics();

  const { t } = useTranslation();
  const {
    activeStep, goTo
  } = useStepper(
    STEP_IDS,
    // @ts-ignore Fix after useStepper is migrated to ts
    STEP_IDS.PHONE_INPUT
  );

  React.useEffect(() => {
    if (isOpen) {
      analytics.viewPhoneNumberVerificationModal();
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen && initialPhoneNumber) {
      setPhoneNumber(initialPhoneNumber);
    }
  }, [initialPhoneNumber, isOpen]);

  const handleClose = () => {
    analytics.closePhoneNumberVerificationModal();
    handleModalClose();
  };

  const handleAfterClose = () => {
    setPhoneNumber('');
    goTo(STEP_IDS.PHONE_INPUT);
    if (onAfterClose) {
      onAfterClose();
    }
  };

  const startTimer = () => {
    const time = new Date();
    time.setSeconds(time.getSeconds() + TIMER_RESEND_SECONDS);
    restartTimer(time);
  };

  const steps = [
    {
      id: STEP_IDS.PHONE_INPUT,
      component: PhoneVerificationModalNumberStep,
      componentProps: {
        phoneNumber,
        isTimerRunning,
        timerTotalSeconds,
        isEdit: mode === 'edit',
        onClose: handleClose,
        onSubmitSuccess: (number: string) => {
          setPhoneNumber(number);
          startTimer();
          goTo(STEP_IDS.CODE_VERIFICATION);
        }
      }
    },
    {
      id: STEP_IDS.CODE_VERIFICATION,
      component: PhoneVerificationModalVerificationStep,
      componentProps: {
        phoneNumber,
        isTimerRunning,
        timerTotalSeconds,
        startTimer,
        onClose: handleClose,
        onBack: () => goTo(STEP_IDS.PHONE_INPUT),
        onSubmitSuccess: () => {
          if (withSuccessNotification) {
            Notifications.showSuccess(
              t('PHONE_VALIDATION.NOTIFICATIONS.SUCCESS'),
              { autoClose: 2000, toastId: 'phoneCodeVerificationSuccess' }
            );
          }
          if (mode === 'edit') {
            analytics.modifyPhoneNumber();
          } else {
            analytics.verifyPhoneNumber();
          }
          if (onSuccess) {
            onSuccess();
          }
          handleClose();
        }
      }
    }
  ];

  return (
    <Modal
      isOpen={isOpen}
      className={classnames(styles.root, className)}
      contentClassName={styles.content}
      breakpoint="md"
      desktopProps={{
        position: 'center',
      }}
      responsiveProps={{
        position: 'bottom',
      }}
      withPadding={false}
      withCloseButton={false}
      close={handleClose}
      onAfterClose={handleAfterClose}
      shouldCloseOnOverlayClick={false}
    >
      <Stepper
        steps={steps}
        activeStep={activeStep}
        className={styles.stepper}
        stepClassName={styles.step}
        contentClassName={styles.stepperContent}
        persist={false}
      />

    </Modal>
  );
};

export default PhoneVerificationModal;
